import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Car, Plus, Pencil, Trash2, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { placa: "", modelo: "", tipo: "utilitario", capacidade: "", status: "ativo", observacoes: "" };
const STATUS_BADGE = {
  ativo: "bg-emerald-100 text-emerald-800",
  inativo: "bg-zinc-100 text-zinc-700",
  manutencao: "bg-amber-100 text-amber-800",
};

export default function Veiculos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/roteirizacao/veiculos", { params: q ? { q } : {} });
      setItems(data?.items || []);
    } catch { toast.error("Falha ao carregar veículos"); }
    setLoading(false);
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const abrirNovo = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const abrirEditar = (v) => { setEditing(v); setForm({ ...EMPTY, ...v }); setOpen(true); };

  const salvar = async () => {
    if (!form.placa?.trim() || !form.modelo?.trim()) { toast.error("Placa e modelo obrigatórios"); return; }
    setBusy(true);
    try {
      if (editing?.id) {
        await api.patch(`/roteirizacao/veiculos/${editing.id}`, form);
        toast.success("Veículo atualizado");
      } else {
        await api.post("/roteirizacao/veiculos", form);
        toast.success("Veículo criado");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };

  const remover = async (v) => {
    if (!window.confirm(`Excluir veículo ${v.placa}?`)) return;
    try {
      await api.delete(`/roteirizacao/veiculos/${v.id}`);
      toast.success("Veículo removido"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha"); }
  };

  return (
    <div className="space-y-4" data-testid="veiculos-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/app/roteirizacao"><Button variant="ghost" size="sm"><ArrowLeft size={14} /></Button></Link>
          <Car size={26} style={{ color: "#1B3A6B" }} />
          <h1 className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>Veículos</h1>
        </div>
        <div className="flex gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar placa/modelo..." className="w-60" data-testid="veiculos-busca" />
          <Button onClick={abrirNovo} className="text-white" style={{ background: "#F97316" }} data-testid="btn-novo-veiculo">
            <Plus size={14} className="mr-1" /> Novo
          </Button>
        </div>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Placa</TableHead>
              <TableHead>Modelo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Capacidade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="animate-spin inline" /></TableCell></TableRow>}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Nenhum veículo cadastrado.</TableCell></TableRow>
            )}
            {!loading && items.map((v) => (
              <TableRow key={v.id} data-testid={`veiculo-row-${v.id}`}>
                <TableCell className="font-mono">{v.placa}</TableCell>
                <TableCell>{v.modelo}</TableCell>
                <TableCell className="text-xs uppercase">{v.tipo}</TableCell>
                <TableCell className="text-xs">{v.capacidade || "—"}</TableCell>
                <TableCell>
                  <Badge className={STATUS_BADGE[v.status] || ""}>{v.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => abrirEditar(v)} data-testid={`editar-${v.id}`}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remover(v)} data-testid={`remover-${v.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="veiculo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Veículo" : "Novo Veículo"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Placa *</Label>
              <Input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })} data-testid="input-placa" placeholder="ABC1D23" />
            </div>
            <div>
              <Label>Modelo *</Label>
              <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} data-testid="input-modelo" placeholder="Sprinter 416" />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="sel-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="caminhao">Caminhão</SelectItem>
                  <SelectItem value="utilitario">Utilitário</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Capacidade</Label>
              <Input value={form.capacidade} onChange={(e) => setForm({ ...form, capacidade: e.target.value })} placeholder="1500kg ou 5m³" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="sel-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                  <SelectItem value="manutencao">Manutenção</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={busy} className="text-white" style={{ background: "#1B3A6B" }} data-testid="btn-salvar-veiculo">
              {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
